/**
 * 園ごとの「よくある質問」（承認済みだけ）。正本はメソッド（method_timee_faq・管理は「タイミー」→「よくある質問」）。
 * フォームの「こんなこと、あおいに聞けますよ！」の質問ボタンと、チャットの最初の質問ボタンに使う。
 * 🟥 取れないとき（承認0件・通信失敗）は、手で書いた3つに戻す。
 */
import { useEffect, useState } from 'react';

export const METHOD_BASE = 'https://method.kirari-hoiku.com';
export type FaqItem = { question: string; answer: string; category: string | null; ask_count?: number };

const FALLBACK = [
  '車で行きます。どこに停めればいいですか',
  'お昼ごはんはどうすればいいですか',
  '着いたらどこから入りますか',
];

// カテゴリの見せ方（正本のカテゴリ名 → ボタンの名前とアイコン）。並びはこの順
export const CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: '駐車場', label: '駐車場・アクセス', icon: 'ri-car-line' },
  { key: '到着・入口', label: '着いたら（入口・チェックイン）', icon: 'ri-door-open-line' },
  { key: '昼食・休憩', label: 'お昼・休憩', icon: 'ri-restaurant-2-line' },
  { key: '服装', label: '服装・持ち物', icon: 'ri-t-shirt-line' },
  { key: '資格証', label: '資格証', icon: 'ri-file-user-line' },
  { key: '初回', label: '初めての方へ', icon: 'ri-seedling-line' },
  { key: '身だしなみ', label: '身だしなみ', icon: 'ri-user-smile-line' },
  { key: '勤務時間', label: '時間・キャンセル', icon: 'ri-time-line' },
  { key: '継続', label: '続けて働きたい', icon: 'ri-hand-heart-line' },
  { key: 'その他', label: 'そのほか', icon: 'ri-question-line' },
];

// 承認済みの質問と答えを、カテゴリごとに（中身のあるカテゴリだけ）
export function useFaq(garden: string): { items: FaqItem[]; groups: { key: string; label: string; icon: string; items: FaqItem[] }[]; loaded: boolean } {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let off = false;
    fetch(`${METHOD_BASE}/api/public/timee-faq?garden=${encodeURIComponent(garden)}`)
      .then((r) => r.json())
      .then((j: { items?: FaqItem[] }) => { if (!off) { setItems(j.items ?? []); setLoaded(true); } })
      .catch(() => { if (!off) setLoaded(true); });
    return () => { off = true; };
  }, [garden]);
  // 🟥 カテゴリはDBにあるものをそのままタイルにする（仕分けエージェントが新しいカテゴリを付けたら、自動でタイルが増える）。
  //    並びは聞かれた回数の合計が多い順＝よく聞かれるものほど上
  const byCat = new Map<string, FaqItem[]>();
  for (const it of items) {
    const k = it.category || 'その他';
    if (!byCat.has(k)) byCat.set(k, []);
    byCat.get(k)!.push(it);
  }
  const groups = [...byCat.entries()].map(([key, list]) => {
    const meta = CATEGORIES.find((c) => c.key === key);
    return { key, label: meta?.label ?? key, icon: meta?.icon ?? 'ri-chat-smile-2-line', items: list, total: list.reduce((a, b) => a + (b.ask_count ?? 1), 0) };
  }).sort((a, b) => (a.key === 'その他' ? 1 : b.key === 'その他' ? -1 : b.total - a.total));
  return { items, groups, loaded };
}

export function useFaqQuestions(garden: string, limit = 4): string[] {
  const [qs, setQs] = useState<string[]>(FALLBACK.slice(0, limit));
  useEffect(() => {
    let off = false;
    fetch(`${METHOD_BASE}/api/public/timee-faq?garden=${encodeURIComponent(garden)}`)
      .then((r) => r.json())
      .then((j: { items?: FaqItem[] }) => {
        const list = (j.items ?? []).map((i) => i.question).filter(Boolean);
        if (!off && list.length) setQs(list.slice(0, limit));
      })
      .catch(() => {});
    return () => { off = true; };
  }, [garden, limit]);
  return qs;
}

// フォームを送った人の名前とメール（あおいが「園に確認します」で引き取るとき、返事の宛先に使う）
const KEY = 'kirari_worker_profile';
export function saveWorkerProfile(p: { name: string; email: string }) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* 保存できなくても困らない */ }
}
export function loadWorkerProfile(): { name: string; email: string } | null {
  try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; }
}
